import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CommentRootModel } from "@repo/structure";
import {
  getProjectEditionDataQuery,
  checkStructureVersionQuery,
  fetchGroupedProjectVersionCommentsQuery,
  getAllLabelsQuery, getCompanyRateCardQuery,
} from "@query";
import {
  PROJECT_COMPANY_SETTINGS,
  USER_COMPANY_SETTINGS,
  COMPANY_SETTINGS,
  EditorSocketProvider,
} from "@client";
import { useStores } from "@hooks";
import { LabelGroups, parseCompanySettings, PROJECT_TYPE } from "@utils";
import { home } from "@paths";
import {
  ESTIMATE_ERROR_CODES,
  EstimatePresetStore,
  EstimateEditorStore,
  EstimateCommentsProvider,
  EstimatePresetStoreProvider,
  EstimateEditorStoreProvider,
  EstimateLoadingAlert,
  EstimateEditorInactivityMonitor,
  EstimateEditorConnectionErrorAlert,
} from "@tools";
import { NewEditorVersionAlert } from "@dialogs";
import { PageCircularProgress } from "@components";
import { ProjectEditorHandlers } from "./ProjectEditorHandlers";

export const ProjectEditor = () => {
  const { t } = useTranslation();
  const { userStore, stateStore } = useStores();
  const navigate = useNavigate();

  const { estimateUuid } = useParams();

  const [ commentStore, setCommentStore ] = useState(null);
  const [ store, setStore ] = useState(null);
  const [ projectHash, setProjectHash ] = useState(null);
  const [ presetStore, setPresetStore ] = useState(null);
  const [ errorCode, setErrorCode ] = useState(null);
  const [ hasNewEditorVersion, setHasNewEditorVersion ] = useState(false);
  
  useEffect(() => {
    document.title = `${t("routes.editor_project")} - Apropo`;
  }, [])

  useEffect(() => {
    setupEditorStore();
    return () => {
      stateStore.resetFilesUploadProgress();
    }
  }, []);

  const loadProjectData = async () => {
    await checkStructureVersionQuery();
    
    try {
      const {
        hash,
        companyLogo,
        companySettings: { defaultPresetId, ...companySettings },
        projectData,
        projectPreset,
        teamMembers,
        isCreated,
      } = await getProjectEditionDataQuery(estimateUuid, [
        USER_COMPANY_SETTINGS.AVERAGE_ALERT(userStore.userUuid),
        USER_COMPANY_SETTINGS.RISK_ALERT(userStore.userUuid),
        USER_COMPANY_SETTINGS.RESIZE_ALERT(userStore.userUuid),
        PROJECT_COMPANY_SETTINGS.VALUE_ALERT(estimateUuid),
        PROJECT_COMPANY_SETTINGS.VALUE_PROJECT(estimateUuid),
        COMPANY_SETTINGS.COMPANY_CURRENCY,
        COMPANY_SETTINGS.CURRENCY_DEF,
        COMPANY_SETTINGS.TIME_EQUIV,
        COMPANY_SETTINGS.COMPANY_CURRENCY,
        COMPANY_SETTINGS.CURRENCY_DEF,
        COMPANY_SETTINGS.PRESET,
      ]);
      
      if(projectData?.name)
        document.title = `${projectData.name} - Apropo`;
      
      const groupedComments = await fetchGroupedProjectVersionCommentsQuery(estimateUuid, userStore.userUuid);
      const companyLabels = await getAllLabelsQuery(LabelGroups.LABELS);
      const customLabels = await getAllLabelsQuery(estimateUuid);
      const workTypes = await getCompanyRateCardQuery();
      
      const timeEquiv = parseCompanySettings(companySettings[COMPANY_SETTINGS.TIME_EQUIV]);
      companySettings.dayModifier = timeEquiv?.day;
      
      return {
        hash,
        isCreated,
        companyLogo,
        groupedComments,
        companySettings,
        companyLabels,
        customLabels,
        workTypes,
        estimateData: projectData,
        teamMembers,
        projectPreset,
        defaultPresetId,
      }
    } catch(e) {
      console.log(e);
      if(e.status === 403)
        navigate(home, { replace: true });
      else throw e;
    }
  };
  
  const reloadProjectData = async () => {
    const { hash, estimateData, isCreated, projectPreset, teamMembers, companySettings } = await loadProjectData();
    setProjectHash(hash);
    if(projectPreset)
      presetStore.setProjectPreset(projectPreset, false);
    store.setTeamMembers(teamMembers);
    store.setCompanySettings(companySettings);
    store.setCreatedStatus(isCreated);
    store.setEstimateData({ estimateData, keepStructures: true });
    return { hash, estimateData, projectPreset, teamMembers };
  };
  
  const setupEditorStore = async () => {
    try {
      const { hash, projectPreset, defaultPresetId, groupedComments, ...data } = await loadProjectData();
      
      setProjectHash(hash);
      setPresetStore(new EstimatePresetStore(projectPreset, defaultPresetId, false, !data.isCreated));
      setCommentStore(CommentRootModel.create({
        commenterData: {
          uuid: userStore.data.uuid,
          fullName: userStore.data.fullname,
          color: userStore.data.avatarColor
        },
        groups: groupedComments,
        useInternalComments: true,
        useExternalComments: false,
      }))
      setStore(new EstimateEditorStore({
        useHistoryManager: true,
        estimateType: PROJECT_TYPE.STANDARD,
        ...data
      }));
    } catch (e) {
      // @todo: check if needed
      // send structure to s3 and add feedback
      // if (e.json) {
      //   if (import.meta.env.APP_NODE_ENV === "production")
      //     await sendBrokenStructureFeedbackFileQuery(e.json);
      //   setError(ESTIMATE_ERROR_CODES.PROJECT);
      //   return;
      // }
      console.log("RELOAD:", e.reload);
      if(e.reload)
        setHasNewEditorVersion(true);
      else if(e.status === 500 && e.response?.data?.error?.[0] === "Record Not Exist")
        setErrorCode(ESTIMATE_ERROR_CODES.PROJECT_DELETED);
      else
        setErrorCode(ESTIMATE_ERROR_CODES.PROJECT_UNKNOWN);
    }
  };
  
  if (errorCode)
    return <EstimateLoadingAlert code={errorCode} />;

  return <>
    {
      !store ? (
        <PageCircularProgress />
      ) : (
        <EstimateCommentsProvider value={commentStore}>
          <EstimateEditorStoreProvider value={store}>
            <EstimatePresetStoreProvider value={presetStore}>
              <EditorSocketProvider reloadProjectData={reloadProjectData}>
                <ProjectEditorHandlers projectHash={projectHash} />
                <EstimateEditorInactivityMonitor reloadEditorData={reloadProjectData} />
                <EstimateEditorConnectionErrorAlert />
              </EditorSocketProvider>
            </EstimatePresetStoreProvider>
          </EstimateEditorStoreProvider>
        </EstimateCommentsProvider>
      )
    }
    {
      hasNewEditorVersion &&
      <NewEditorVersionAlert />
    }
  </>
};
